import { MouseEventHandler } from "react";
import { Button, ButtonProps } from "@chakra-ui/react";
import { MutationResult } from "@apollo/client";
import { useBrandMutation } from "utils/apollo/hooks/brand";
import { gql, DocumentType } from "__generated__/gql";
import { asyncVoid } from "utils/asyncVoidHandler";
import PlusButton from "components/Shared/PlusButton";
import { useCurrentBrandId } from "utils/currentUserHooks";
import { assertBrandId, assertPresence } from "utils/assertions";
import { addNewRecordsWithFieldSpec } from "utils/apollo/cacheHelpers";
import useAsyncCompleteToast from "components/Shared/useAsyncCompleteToast";

const cacheUpdateFragment = gql(/* GraphQL */ `
  fragment AddBrandOrderingPlatformCacheUpdate on Query {
    brandOrderingPlatformByBrandIdAndOrderingPlatformId(
      brandId: $brandId
      orderingPlatformId: $orderingPlatformId
    ) {
      id
    }
  }
`);

const mutation = gql(/* GraphQL */ `
  mutation AddBrandOrderingPlatformButton($data: BrandOrderingPlatformInput!) {
    createBrandOrderingPlatform(input: { brandOrderingPlatform: $data }) {
      brandOrderingPlatform {
        id
        brandId
        orderingPlatformId
        orderingPlatform {
          id
          name
        }
        ...CustomerPageBrandDetails
        ...BrandCustomersTableRow
      }
    }
  }
`);

export function useAddBrandOrderingPlatform(
  orderingPlatformId: UUID,
): [
  MouseEventHandler<HTMLButtonElement>,
  MutationResult<DocumentType<typeof mutation>>,
] {
  const brandId = useCurrentBrandId();

  const [addBrandOrderingPlatform, mutationResult] = useBrandMutation(
    mutation,
    {
      variables: brandId
        ? { data: { brandId, orderingPlatformId } }
        : undefined,
      update: (cache, result) => {
        const newBcr =
          result.data?.createBrandOrderingPlatform?.brandOrderingPlatform;

        assertPresence(newBcr);

        addNewRecordsWithFieldSpec({
          cache,
          baseFieldName: "brandOrderingPlatforms",
          newObjs: [newBcr],
          getArgs: (existing) => ({
            ...existing,
            condition: { brandId: newBcr.brandId },
          }),
        });

        cache.writeFragment({
          fragment: cacheUpdateFragment,
          variables: {
            brandId: newBcr.brandId,
            orderingPlatformId: newBcr.orderingPlatformId,
          },
          data: {
            brandOrderingPlatformByBrandIdAndOrderingPlatformId: {
              id: newBcr.id,
              __typename: "BrandOrderingPlatform",
            },
            __typename: "Query",
          },
        });
      },
    },
  );

  const { showSuccessToast, showErrorToast } = useAsyncCompleteToast();

  const handleClick: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation();
    assertBrandId(brandId);
    asyncVoid(
      addBrandOrderingPlatform()
        .then((result) =>
          showSuccessToast(
            <>
              <b>
                “
                {
                  result.data?.createBrandOrderingPlatform
                    ?.brandOrderingPlatform?.orderingPlatform?.name
                }
                ”
              </b>{" "}
              added successfully
            </>,
          ),
        )
        .catch(showErrorToast),
    );
  };

  return [handleClick, mutationResult];
}

type PlusButtonProps = {
  orderingPlatformId: UUID;
};

export function AddOrderingPlatformPlusButton({
  orderingPlatformId,
}: PlusButtonProps) {
  const [handleClick, { loading }] =
    useAddBrandOrderingPlatform(orderingPlatformId);
  return (
    <PlusButton
      label="Add customer"
      onClick={handleClick}
      isLoading={loading}
    />
  );
}

type Props = ButtonProps & {
  orderingPlatformId: UUID;
};

export default function AddOrderingPlatformButton({
  orderingPlatformId,
  ...rest
}: Props) {
  const [handleClick, { loading }] =
    useAddBrandOrderingPlatform(orderingPlatformId);
  return <Button {...rest} onClick={handleClick} isLoading={loading} />;
}
